/**
 * File hand-off for the watchlist export (PV18, SE-8360) — web only in v1.
 *
 * CSV goes out as a Blob through an anchor download; PDF through
 * `pdf.save`. The hook decides whether the platform can hand a file off at
 * all; these writers assume a browser.
 * @module features/scouting/utils/watchlistExport/writeExportFile
 */

import type { ExportMatrix } from "./exportMatrix";
import {
  renderWatchlistPdf,
  type WatchlistPdfMeta,
} from "./renderWatchlistPdf";
import { toCsv } from "./toCsv";

/**
 * Offers a Blob to the browser as a download.
 * @param filename - The suggested filename
 * @param blob - The file body
 */
const downloadBlob = (filename: string, blob: Blob): void => {
  const url = URL.createObjectURL(blob);
  const anchor = Object.assign(document.createElement("a"), {
    href: url,
    download: filename,
    rel: "noopener",
  });
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

/**
 * Writes the matrix as a CSV download.
 * @param matrix - Header and rows from `buildExportMatrix`
 * @param filename - The suggested filename
 */
/**
 * Whether the run that asked for the file is still the current one; a
 * retired run (cancelled, or superseded by a second start) writes nothing.
 */
export type IsCurrentRun = () => boolean;

export const writeCsvFile = async (
  matrix: ExportMatrix,
  filename: string,
  isCurrent: IsCurrentRun = () => true
): Promise<void> => {
  if (!isCurrent()) return;
  downloadBlob(
    filename,
    new Blob([toCsv(matrix)], { type: "text/csv;charset=utf-8" })
  );
};

/**
 * Writes the matrix as a PDF download.
 * @param matrix - Header and rows from `buildExportMatrix`
 * @param meta - The title line
 * @param filename - The suggested filename
 * @param isCurrent - Whether the asking run is still current; checked before
 * and after the render, which awaits the jspdf import
 */
export const writePdfFile = async (
  matrix: ExportMatrix,
  meta: WatchlistPdfMeta,
  filename: string,
  isCurrent: IsCurrentRun = () => true
): Promise<void> => {
  if (!isCurrent()) return;
  const pdf = await renderWatchlistPdf(matrix, meta);
  if (!isCurrent()) return;
  pdf.save(filename);
};
